/**
 * Compartilhar a minha posição enquanto o app está aberto.
 *
 * É o que uma PWA consegue: `watchPosition` só corre com a página viva. O
 * produto assume isto e diz "há X min" em vez de prometer segundo plano.
 *
 * Ligar pede permissão ao navegador; negada, o mapa entra no estado
 * "desligado" e explica a reciprocidade. Ligado, cada leitura passa pela
 * regra `deveEnviar` (tempo OU deslocamento) antes de ir ao servidor.
 * Desligar limpa o watch E apaga a posição no servidor.
 *
 * A escolha "ligado" fica por viagem no aparelho: reabrir o app volta a
 * compartilhar sem perguntar de novo — a pessoa já decidiu.
 */
import { create } from 'zustand';

import { deveEnviar, type Ponto } from './regras';

export type Permissao = 'desconhecida' | 'pedindo' | 'concedida' | 'negada' | 'indisponivel';

interface Estado {
  tripId: string | null;
  ativo: boolean;
  permissao: Permissao;
  ultima: (Ponto & { accuracyM: number | null; at: number }) | null;
  erro: string | null;
  ligar: (tripId: string) => Promise<void>;
  desligar: () => Promise<void>;
  /** Retoma o que estava ligado antes (ao abrir o app). */
  retomar: (tripId: string) => void;
}

const chave = (tripId: string) => `nv.mapa.compartilhar:${tripId}`;

function lembrar(tripId: string, ativo: boolean) {
  try {
    if (ativo) localStorage.setItem(chave(tripId), '1');
    else localStorage.removeItem(chave(tripId));
  } catch {
    // Sem storage, a escolha vale só nesta abertura.
  }
}

function lembrado(tripId: string): boolean {
  try {
    return localStorage.getItem(chave(tripId)) === '1';
  } catch {
    return false;
  }
}

let watchId: number | null = null;
let ultimoEnvio: { at: number; ponto: Ponto } | null = null;

async function enviar(tripId: string, p: GeolocationPosition): Promise<void> {
  const corpo = {
    latitude: p.coords.latitude,
    longitude: p.coords.longitude,
    accuracyM: Number.isFinite(p.coords.accuracy) ? p.coords.accuracy : null,
    recordedAt: new Date(p.timestamp).toISOString(),
  };
  const res = await fetch(`/api/v1/trips/${tripId}/me/location`, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(corpo),
  });
  if (!res.ok) throw new Error(`Falha ao enviar a posição (${res.status})`);
  // A tela do mapa relê as posições na hora, em vez de esperar o próximo
  // ciclo — a própria pessoa precisa se ver assim que liga.
  window.dispatchEvent(new Event('nv:posicao-enviada'));
}

function pararWatch() {
  if (watchId !== null && typeof navigator !== 'undefined' && navigator.geolocation) {
    navigator.geolocation.clearWatch(watchId);
  }
  watchId = null;
  ultimoEnvio = null;
}

export const useCompartilharPosicao = create<Estado>((set, get) => ({
  tripId: null,
  ativo: false,
  permissao: 'desconhecida',
  ultima: null,
  erro: null,

  ligar: async (tripId) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      set({ permissao: 'indisponivel', ativo: false });
      return;
    }
    pararWatch();
    set({ tripId, permissao: 'pedindo', erro: null });

    await new Promise<void>((resolve) => {
      watchId = navigator.geolocation.watchPosition(
        (p) => {
          const leitura = { at: p.timestamp, ponto: { latitude: p.coords.latitude, longitude: p.coords.longitude } };
          set({
            permissao: 'concedida',
            ativo: true,
            ultima: { ...leitura.ponto, accuracyM: p.coords.accuracy ?? null, at: p.timestamp },
          });
          lembrar(tripId, true);
          if (deveEnviar(ultimoEnvio, leitura)) {
            ultimoEnvio = leitura;
            void enviar(tripId, p).catch((e: unknown) => {
              set({ erro: e instanceof Error ? e.message : 'Falha ao enviar a posição.' });
            });
          }
          resolve();
        },
        (e) => {
          // PERMISSION_DENIED = 1. Os outros (posição indisponível, tempo
          // esgotado) não são "não": a pessoa deixou, o aparelho é que falhou.
          if (e.code === 1) {
            pararWatch();
            lembrar(tripId, false);
            set({ permissao: 'negada', ativo: false });
          } else {
            set({ erro: 'Não foi possível obter a sua posição agora.' });
          }
          resolve();
        },
        { enableHighAccuracy: true, maximumAge: 15_000, timeout: 20_000 },
      );
    });
  },

  desligar: async () => {
    const { tripId } = get();
    pararWatch();
    if (tripId) lembrar(tripId, false);
    set({ ativo: false, ultima: null, erro: null });
    if (tripId) {
      await fetch(`/api/v1/trips/${tripId}/me/location`, { method: 'DELETE', credentials: 'same-origin' }).catch(
        () => undefined,
      );
    }
  },

  retomar: (tripId) => {
    const s = get();
    if (s.ativo && s.tripId === tripId) return;
    if (s.tripId && s.tripId !== tripId) {
      // Trocou de viagem: o que estava ligado era da outra.
      pararWatch();
      set({ ativo: false, ultima: null, tripId: null });
    }
    if (lembrado(tripId)) void get().ligar(tripId);
  },
}));
